import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Stack,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Button,
  Text,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  useToast,
  Spinner,
  Center,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  Icon,
  Image,
  Collapse,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import {
  FiSave,
  FiRefreshCw,
  FiSettings,
  FiMenu,
  FiUpload,
  FiClock,
  FiMail,
  FiBell,
  FiShield,
  FiZap,
  FiDollarSign,
  FiUsers,
  FiEye,
  FiToggleLeft,
  FiSidebar,
  FiCalendar,
  FiLock,
  FiGrid,
  FiBriefcase,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";
import { HiOfficeBuilding } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import {
  getSidebarModules,
  getAllSidebarModules,
  updateSidebarModules,
  getSidebarOverrides,
  updateSidebarOverrides,
  deleteSidebarOverrides,
  getSidebarRoles,
} from "../services/sidebarService";
import {
  getSettings,
  updateSettings,
  resetSettings as resetSettingsAPI,
} from "../services/settingsService";
import { BASE_URL } from "../services/api";
import {
  getCurrentCompany,
  updateCompanySettings as updateCompanySettingsAPI,
  uploadCompanyLogo,
} from "../services/companyService";

const formatAddressText = (address = {}) => {
  return [
    address.street,
    address.city,
    address.state,
    address.country,
    address.zipCode,
  ]
    .filter(Boolean)
    .join(", ");
};

const parseAddressText = (addressText = "") => {
  const parts = addressText
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return {
    street: parts[0] || "",
    city: parts[1] || "",
    state: parts[2] || "",
    country: parts[3] || "",
    zipCode: parts[4] || "",
  };
};

const resolveAssetUrl = (assetUrl) => {
  if (!assetUrl) return "";
  if (assetUrl.startsWith("http://") || assetUrl.startsWith("https://")) {
    return assetUrl;
  }

  return `${BASE_URL}${assetUrl}`;
};

const persistCompanyLocally = (company) => {
  const storedCompany = JSON.parse(localStorage.getItem("company") || "{}");
  localStorage.setItem(
    "company",
    JSON.stringify({
      ...storedCompany,
      ...company,
      id: company.id || company._id || storedCompany.id,
    }),
  );
  // Notify same-tab listeners (window.storage only fires cross-tab)
  window.dispatchEvent(new Event("company-updated"));
};

const Settings = () => {
  const navigate = useNavigate();

  // Dark mode colors
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const isMobileRbac = useBreakpointValue({ base: true, lg: false });

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});
  const [sidebarModules, setSidebarModules] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [sidebarOverrides, setSidebarOverrides] = useState(null);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [rolePermissions, setRolePermissions] = useState({});
  const [rbacExpandedModules, setRbacExpandedModules] = useState({});
  const [rbacRoleFilter, setRbacRoleFilter] = useState("ALL");
  const [activeTab, setActiveTab] = useState(0);
  const [company, setCompany] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const toast = useToast();

  useEffect(() => {
    fetchPageData();
  }, []);

  useEffect(() => {
    if (activeTab === 12) {
      fetchSidebarModules();
      fetchSidebarOverrides();
      fetchRolePermissions();
    }
  }, [activeTab]);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const [settingsResponse, companyResponse] = await Promise.all([
        getSettings(),
        getCurrentCompany(),
      ]);

      if (settingsResponse.success) {
        setSettings(settingsResponse.data.settings);
        setCategories(settingsResponse.data.categories);
      }

      if (companyResponse.success) {
        const currentCompany = companyResponse.data.company;
        const nextCompany = {
          ...currentCompany,
          addressText: formatAddressText(currentCompany.address),
        };
        setCompany(nextCompany);
        const uploadedLogoPreview = resolveAssetUrl(
          currentCompany.branding?.logo || "",
        );
        setLogoPreview(
          uploadedLogoPreview || currentCompany.branding?.logoUrl || "",
        );
        persistCompanyLocally(currentCompany);
      }
    } catch (error) {
      console.error("Error fetching settings/company:", error);
      toast({
        title: "Error",
        description: "Failed to load settings",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyField = (path, value) => {
    setCompany((prev) => {
      const nextCompany = { ...prev };
      const keys = path.split(".");
      let current = nextCompany;

      for (let index = 0; index < keys.length - 1; index++) {
        if (!current[keys[index]]) current[keys[index]] = {};
        current = current[keys[index]];
      }

      current[keys[keys.length - 1]] = value;
      return nextCompany;
    });
  };

  const handleLogoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const updateSetting = (path, value) => {
    setSettings((prev) => {
      const newSettings = { ...prev };
      const keys = path.split(".");
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      if (company?._id) {
        const companyPayload = {
          name: company.name,
          contactEmail: company.contactEmail,
          contactPhone: company.contactPhone,
          address: parseAddressText(company.addressText),
          branding: {
            ...company.branding,
            companyWebsite: company.branding?.companyWebsite || "",
          },
        };

        const companyResponse = await updateCompanySettingsAPI(
          company._id,
          companyPayload,
        );

        if (companyResponse.success && companyResponse.data?.company) {
          persistCompanyLocally(companyResponse.data.company);
        }

        if (logoFile) {
          const logoResponse = await uploadCompanyLogo(company._id, logoFile);
          if (logoResponse.success) {
            setLogoFile(null);
          }
        }
      }

      const response = await updateSettings(settings);
      if (response.success) {
        await fetchPageData();
        toast({
          title: "Success",
          description: "Settings saved successfully",
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to save settings",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    try {
      const response = await resetSettingsAPI();
      if (response.success) {
        setSettings(response.data);
        toast({
          title: "Success",
          description: "Settings reset to defaults",
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error resetting settings:", error);
      toast({
        title: "Error",
        description: "Failed to reset settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  const fetchSidebarModules = async () => {
    try {
      setSidebarLoading(true);
      // Use admin endpoint to get full module data with isActive status
      const data = await getAllSidebarModules();
      setSidebarModules(data || []);
    } catch (error) {
      console.error("Error fetching sidebar modules:", error);
    } finally {
      setSidebarLoading(false);
    }
  };

  const toggleModuleExpanded = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const getFeatureGroupLabel = (item) => {
    const fingerprint =
      `${item?.key || ""} ${item?.label || ""} ${item?.to || ""}`.toLowerCase();

    if (/edos/.test(fingerprint)) {
      return "Enterprise Delivery OS";
    }
    if (
      /(employee|aadhaar|pan|profile|personal|database|staff)/.test(fingerprint)
    ) {
      return "Employee Data";
    }
    if (/(document|file|upload|digital)/.test(fingerprint)) {
      return "Documents";
    }
    if (/(rbac|role|permission|access)/.test(fingerprint)) {
      return "Access Control";
    }
    if (
      /(setting|config|system|integration|feature|appearance)/.test(fingerprint)
    ) {
      return "System";
    }
    if (/(attendance|leave|payroll|recruit|ticket|support)/.test(fingerprint)) {
      return "Operations";
    }

    return "General";
  };

  const getGroupedModuleItems = (items = []) => {
    return items.reduce((acc, item) => {
      const group = getFeatureGroupLabel(item);
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});
  };

  const getModuleEnabledCount = (module) => {
    return (module.items || []).filter((item) => item.isActive !== false)
      .length;
  };

  const setModuleItemsActive = async (moduleId, nextActive) => {
    try {
      const updatedModules = sidebarModules.map((m) => {
        if (m._id !== moduleId) return m;
        return {
          ...m,
          items: (m.items || []).map((item) => ({
            ...item,
            isActive: nextActive,
          })),
        };
      });

      await updateSidebarModules(updatedModules);
      await fetchSidebarModules();
      window.dispatchEvent(new Event("sidebar-toggle"));

      toast({
        title: nextActive ? "All features enabled" : "All features disabled",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating features",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleModuleActive = async (moduleId) => {
    try {
      const updatedModules = sidebarModules.map((m) =>
        m._id === moduleId ? { ...m, isActive: !m.isActive } : m,
      );
      await updateSidebarModules(updatedModules);

      // Refresh modules from server to ensure consistency
      await fetchSidebarModules();
      window.dispatchEvent(new Event("sidebar-toggle"));

      toast({
        title: "Module updated",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating module",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleItemActive = async (moduleId, itemIndex) => {
    try {
      const targetModule = sidebarModules.find((m) => m._id === moduleId);
      if (targetModule && targetModule.isActive === false) {
        toast({
          title: "Module is inactive",
          description:
            "Enable the module first before changing feature-level states.",
          status: "warning",
          duration: 2000,
          isClosable: true,
        });
        return;
      }

      const updatedModules = sidebarModules.map((m) => {
        if (m._id === moduleId) {
          const updatedItems = m.items.map((item, idx) =>
            idx === itemIndex ? { ...item, isActive: !item.isActive } : item,
          );
          return { ...m, items: updatedItems };
        }
        return m;
      });
      await updateSidebarModules(updatedModules);

      // Refresh modules from server to ensure consistency
      await fetchSidebarModules();
      window.dispatchEvent(new Event("sidebar-toggle"));

      toast({
        title: "Item updated",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({});
    }
  };

  const fetchSidebarOverrides = async () => {
    try {
      setOverridesLoading(true);
      const data = await getSidebarOverrides();
      setSidebarOverrides(data || { overrides: [] });
    } catch (error) {
      console.error("Error fetching sidebar overrides:", error);
      setSidebarOverrides({ overrides: [] });
    } finally {
      setOverridesLoading(false);
    }
  };

  const fetchRolePermissions = async () => {
    try {
      const data = await getSidebarRoles();
      setRolePermissions(data || {});
    } catch (error) {
      console.error("Error fetching role permissions:", error);
    }
  };

  const getRolesForItem = (itemPermissions) => {
    if (!itemPermissions || itemPermissions.length === 0) {
      return Object.keys(rolePermissions);
    }
    return Object.entries(rolePermissions)
      .filter(([, roleMeta]) => {
        const perms = roleMeta.permissions || [];
        if (perms.includes("*")) return true;
        return itemPermissions.some((itemPerm) =>
          perms.some((rolePerm) => {
            if (rolePerm === itemPerm) return true;
            if (rolePerm.endsWith(".*")) {
              return itemPerm.startsWith(rolePerm.slice(0, -2));
            }
            return false;
          }),
        );
      })
      .map(([role]) => role);
  };

  const getItemOverride = (itemKey) => {
    if (!itemKey || !sidebarOverrides?.overrides) return null;
    return (
      sidebarOverrides.overrides.find((o) => o.itemKey === itemKey) || null
    );
  };

  const getVisibleRolesForItem = (item) => {
    const override = getItemOverride(item.key);

    if (!override || !Array.isArray(override.visibleRoles)) {
      return getRolesForItem(item.permissions);
    }

    return override.visibleRoles;
  };

  const persistItemOverride = async (itemKey, updater, successTitle) => {
    try {
      const currentOverrides = sidebarOverrides?.overrides || [];
      const existingIndex = currentOverrides.findIndex(
        (o) => o.itemKey === itemKey,
      );
      const currentItem =
        existingIndex >= 0
          ? currentOverrides[existingIndex]
          : { itemKey, isVisible: true, isEnabled: true };

      const nextItem = updater(currentItem);
      let updatedOverrides;

      if (
        nextItem.isVisible !== false &&
        nextItem.isEnabled !== false &&
        !Array.isArray(nextItem.visibleRoles)
      ) {
        updatedOverrides = currentOverrides.filter(
          (o) => o.itemKey !== itemKey,
        );
      } else if (existingIndex >= 0) {
        updatedOverrides = currentOverrides.map((o, idx) =>
          idx === existingIndex ? nextItem : o,
        );
      } else {
        updatedOverrides = [...currentOverrides, nextItem];
      }

      await updateSidebarOverrides(company._id || company.id, updatedOverrides);
      await fetchSidebarOverrides();

      toast({
        title: successTitle,
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating override",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleItemVisibility = async (itemKey) => {
    await persistItemOverride(
      itemKey,
      (currentItem) => ({
        ...currentItem,
        isVisible: currentItem.isVisible === false,
      }),
      "Visibility updated",
    );
  };

  const toggleItemRoleVisibility = async (item, role) => {
    const matrixRoles = Object.keys(rolePermissions || {});
    if (!matrixRoles.includes(role)) return;

    const visibleRoleSet = new Set(getVisibleRolesForItem(item));
    if (visibleRoleSet.has(role)) {
      visibleRoleSet.delete(role);
    } else {
      visibleRoleSet.add(role);
    }

    const nextVisibleRoles = matrixRoles.filter((r) => visibleRoleSet.has(r));

    await persistItemOverride(
      item.key,
      (currentItem) => ({
        ...currentItem,
        // Keep row-level visibility in sync with role matrix state.
        isVisible: nextVisibleRoles.length > 0,
        visibleRoles: nextVisibleRoles,
      }),
      "Role visibility updated",
    );
  };

  const applyModuleRolePreset = async (module, mode, selectedRole = null) => {
    try {
      const currentOverrides = sidebarOverrides?.overrides || [];
      const nextOverridesMap = new Map(
        currentOverrides.map((override) => [override.itemKey, { ...override }]),
      );

      (module.items || []).forEach((item) => {
        if (!item.key) return;

        const eligibleRoles = getRolesForItem(item.permissions);
        const existing = nextOverridesMap.get(item.key) || {
          itemKey: item.key,
          isVisible: true,
          isEnabled: true,
        };

        if (mode === "all") {
          const { visibleRoles, ...rest } = existing;
          nextOverridesMap.set(item.key, rest);
          return;
        }

        if (mode === "none") {
          nextOverridesMap.set(item.key, {
            ...existing,
            visibleRoles: [],
          });
          return;
        }

        if (mode === "only" && selectedRole) {
          nextOverridesMap.set(item.key, {
            ...existing,
            visibleRoles: eligibleRoles.includes(selectedRole)
              ? [selectedRole]
              : [],
          });
        }
      });

      const updatedOverrides = Array.from(nextOverridesMap.values());

      await updateSidebarOverrides(company._id || company.id, updatedOverrides);
      await fetchSidebarOverrides();

      toast({
        title: "Module role preset applied",
        status: "success",
        duration: 1800,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error applying module preset",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleRbacModuleExpanded = (moduleId) => {
    setRbacExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const getRoleColumns = () => {
    const allRoles = Object.keys(rolePermissions);
    if (rbacRoleFilter === "ALL") return allRoles;
    return allRoles.filter((role) => role === rbacRoleFilter);
  };

  const persistOverridesBatch = async (updatedOverrides, successTitle) => {
    try {
      await updateSidebarOverrides(company._id || company.id, updatedOverrides);
      await fetchSidebarOverrides();

      toast({
        title: successTitle,
        status: "success",
        duration: 1600,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating overrides",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const applyGlobalRolePreset = async (mode) => {
    const currentOverrides = sidebarOverrides?.overrides || [];
    const nextOverridesMap = new Map(
      currentOverrides.map((override) => [override.itemKey, { ...override }]),
    );

    sidebarModules.forEach((module) => {
      (module.items || []).forEach((item) => {
        if (!item.key) return;

        const eligibleRoles = getRolesForItem(item.permissions);
        const existing = nextOverridesMap.get(item.key) || {
          itemKey: item.key,
          isVisible: true,
          isEnabled: true,
        };

        if (mode === "all") {
          // Enable for all ELIGIBLE roles - remove override to use permission-based defaults
          const { visibleRoles, ...rest } = existing;
          nextOverridesMap.set(item.key, rest);
          return;
        }

        if (mode === "none") {
          nextOverridesMap.set(item.key, {
            ...existing,
            isVisible: false,
            visibleRoles: [],
          });
          return;
        }

        // Default: enable for all eligible roles
        const { visibleRoles, ...rest } = existing;
        nextOverridesMap.set(item.key, rest);
      });
    });

    await persistOverridesBatch(
      Array.from(nextOverridesMap.values()),
      mode === "all" ? "All features enabled" : "All features disabled",
    );
  };

  const setFeatureAllRoles = async (item, enabled) => {
    if (!item.key) return;
    const eligibleRoles = getRolesForItem(item.permissions);

    await persistItemOverride(
      item.key,
      (currentItem) => {
        if (enabled) {
          // Enable for all ELIGIBLE roles - remove override to use permission-based defaults
          const { visibleRoles, ...rest } = currentItem;
          return {
            ...rest,
            isVisible: true,
          };
        } else {
          // Disable for all roles
          return {
            ...currentItem,
            isVisible: false,
            visibleRoles: [],
          };
        }
      },
      enabled
        ? "Feature enabled for all roles"
        : "Feature disabled for all roles",
    );
  };

  const setRoleAcrossModule = async (module, role, enabled) => {
    const currentOverrides = sidebarOverrides?.overrides || [];
    const nextOverridesMap = new Map(
      currentOverrides.map((override) => [override.itemKey, { ...override }]),
    );

    (module.items || []).forEach((item) => {
      if (!item.key) return;

      const matrixRoles = Object.keys(rolePermissions || {});
      if (!matrixRoles.includes(role)) return;

      const eligibleRoles = getRolesForItem(item.permissions);
      const existing = nextOverridesMap.get(item.key) || {
        itemKey: item.key,
        isVisible: true,
        isEnabled: true,
      };

      // Use eligible roles as default if no override exists
      const currentVisible = Array.isArray(existing.visibleRoles)
        ? existing.visibleRoles.filter((r) => matrixRoles.includes(r))
        : eligibleRoles;

      const nextVisibleSet = new Set(currentVisible);
      if (enabled) {
        // Only add role if it's eligible for this item
        if (eligibleRoles.includes(role)) {
          nextVisibleSet.add(role);
        }
      } else {
        nextVisibleSet.delete(role);
      }

      const nextVisibleRoles = matrixRoles.filter((r) => nextVisibleSet.has(r));

      // If the next visible roles match the eligible roles, remove the override
      const isDefaultState =
        nextVisibleRoles.length === eligibleRoles.length &&
        nextVisibleRoles.every((r) => eligibleRoles.includes(r));

      if (isDefaultState) {
        // Remove override to use permission-based defaults
        const { visibleRoles, ...rest } = existing;
        nextOverridesMap.set(item.key, rest);
      } else {
        nextOverridesMap.set(item.key, {
          ...existing,
          isVisible: nextVisibleRoles.length > 0,
          visibleRoles: nextVisibleRoles,
        });
      }
    });

    await persistOverridesBatch(
      Array.from(nextOverridesMap.values()),
      enabled ? `${role} enabled for module` : `${role} disabled for module`,
    );
  };

  const resetAllOverrides = async () => {
    try {
      await deleteSidebarOverrides();

      // Refresh overrides from server to ensure consistency
      await fetchSidebarOverrides();

      toast({
        title: "Overrides reset",
        description: "All items are now visible",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error resetting overrides",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getItemVisibility = (itemKey) => {
    if (!sidebarOverrides?.overrides) return true;
    const override = sidebarOverrides.overrides.find(
      (o) => o.itemKey === itemKey,
    );
    return override ? override.isVisible !== false : true;
  };

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading settings...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Container maxW="100%" py={2}>
      <VStack spacing={6} align="stretch">
        <Stack
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          spacing={{ base: 4, md: 0 }}
        >
          <HStack>
            <Heading size="lg">System Settings</Heading>
          </HStack>
          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={{ base: 2, sm: 4 }}
            width={{ base: "full", md: "auto" }}
          >
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={resetSettings}
              colorScheme="red"
              variant="outline"
              width={{ base: "full", sm: "auto" }}
            >
              Reset to Defaults
            </Button>
            <Button
              leftIcon={<FiSave />}
              onClick={saveSettings}
              colorScheme="blue"
              isLoading={saving}
              loadingText="Saving..."
              width={{ base: "full", sm: "auto" }}
            >
              Save Changes
            </Button>
          </Stack>
        </Stack>

        <Alert status="info">
          <AlertIcon />
          <Box>
            <AlertTitle>Settings Management</AlertTitle>
            <AlertDescription>
              Configure your HR system preferences, integrations, and
              organizational settings. Changes take effect immediately after
              saving.
            </AlertDescription>
          </Box>
        </Alert>

        <Tabs
          orientation={{ base: "horizontal", lg: "vertical" }}
          variant="soft-rounded"
          colorScheme="blue"
          index={activeTab}
          onChange={setActiveTab}
          isLazy
          lazyBehavior="unmount"
          align="start"
        >
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={{ base: 4, lg: 6 }}
            align="stretch"
          >
            {/* Tab Navigation */}
            <Box
              minW={{ base: "full", lg: "240px" }}
              maxW={{ base: "full", lg: "240px" }}
              position={{ base: "sticky", lg: "sticky" }}
              top={{ base: "0px", lg: "20px" }}
              zIndex={{ base: 10, lg: 1 }}
              alignSelf="start"
              bg={{ base: bgColor, lg: "transparent" }}
              py={{ base: 2, lg: 0 }}
            >
              <TabList
                flexDirection={{ base: "row", lg: "column" }}
                overflowX={{ base: "auto", lg: "visible" }}
                overflowY={{ base: "hidden", lg: "visible" }}
                flexWrap="nowrap"
                gap={{ base: 1, lg: 2 }}
                p={{ base: 1.5, lg: 2 }}
                bg={cardBg}
                borderRadius={{ base: "md", lg: "lg" }}
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow={{ base: "sm", lg: "none" }}
                css={{
                  "&::-webkit-scrollbar": {
                    height: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#CBD5E0",
                    borderRadius: "2px",
                  },
                  scrollbarWidth: "thin",
                  WebkitOverflowScrolling: "touch",
                  scrollSnapType: "x mandatory",
                }}
              >
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={HiOfficeBuilding} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Company</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiSettings} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>System</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiClock} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Attendance</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiCalendar} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Leave</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiMail} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Email</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiBell} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>
                      Notifications
                    </Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiShield} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Security</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiZap} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>
                      Integrations
                    </Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiDollarSign} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Payroll</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiBriefcase} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Recruitment</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiEye} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Appearance</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiToggleLeft} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Features</Text>
                  </HStack>
                </Tab>
                <Tab
                  justifyContent={{ base: "center", lg: "flex-start" }}
                  px={{ base: 3, lg: 4 }}
                  py={{ base: 2, lg: 3 }}
                  minW={{ base: "fit-content", lg: "full" }}
                  whiteSpace="nowrap"
                  scrollSnapAlign="start"
                  _selected={{
                    bg: "blue.500",
                    color: "white",
                    fontWeight: "semibold",
                  }}
                  _hover={{
                    bg: useColorModeValue("gray.100", "gray.600"),
                    _selected: { bg: "blue.500" },
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={{ base: 2, lg: 3 }} w="full">
                    <Icon as={FiSidebar} boxSize={{ base: 4, lg: 5 }} />
                    <Text fontSize={{ base: "sm", lg: "md" }}>Sidebar</Text>
                  </HStack>
                </Tab>
              </TabList>
            </Box>

            {/* Tab Content */}
            <Box flex="1" minW="0">
              <TabPanels>
                {/* Company Information */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Company Information</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Company Name</FormLabel>
                          <Input
                            value={company?.name || ""}
                            onChange={(e) =>
                              updateCompanyField("name", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Company Email</FormLabel>
                          <Input
                            type="email"
                            value={company?.contactEmail || ""}
                            onChange={(e) =>
                              updateCompanyField("contactEmail", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Company Phone</FormLabel>
                          <Input
                            value={company?.contactPhone || ""}
                            onChange={(e) =>
                              updateCompanyField("contactPhone", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Company Website</FormLabel>
                          <Input
                            value={company?.branding?.companyWebsite || ""}
                            onChange={(e) =>
                              updateCompanyField(
                                "branding.companyWebsite",
                                e.target.value,
                              )
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Company Logo</FormLabel>
                          <VStack align="stretch" spacing={3}>
                            <Box
                              border="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                              p={4}
                              bg={useColorModeValue("gray.50", "gray.700")}
                            >
                              {logoPreview ? (
                                <Image
                                  src={logoPreview}
                                  alt="Company logo"
                                  maxH="90px"
                                  objectFit="contain"
                                />
                              ) : (
                                <Text color={mutedColor}>No logo uploaded</Text>
                              )}
                            </Box>
                            <Button
                              as="label"
                              leftIcon={<FiUpload />}
                              variant="outline"
                              cursor="pointer"
                            >
                              Choose Logo
                              <Input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                display="none"
                                onChange={handleLogoSelect}
                              />
                            </Button>
                            <Text fontSize="sm" color={mutedColor}>
                              PNG, JPG, WEBP, or SVG. Max file size 5 MB.
                            </Text>
                            {logoFile && (
                              <Badge colorScheme="green" w="fit-content">
                                Selected: {logoFile.name}
                              </Badge>
                            )}
                            <Divider />
                            <FormControl>
                              <FormLabel fontSize="sm" mb={1}>
                                Or enter Logo URL
                              </FormLabel>
                              <Input
                                size="sm"
                                placeholder="https://example.com/logo.png"
                                value={company?.branding?.logoUrl || ""}
                                onChange={(e) => {
                                  updateCompanyField(
                                    "branding.logoUrl",
                                    e.target.value,
                                  );
                                  if (!logoFile) {
                                    setLogoPreview(e.target.value);
                                  }
                                }}
                              />
                              <Text fontSize="xs" color={mutedColor} mt={1}>
                                A direct URL to the logo hosted on your server
                                or CDN. Used when no file is uploaded.
                              </Text>
                            </FormControl>
                          </VStack>
                        </FormControl>
                        <FormControl gridColumn="span 2">
                          <FormLabel>Company Address</FormLabel>
                          <Textarea
                            value={company?.addressText || ""}
                            onChange={(e) =>
                              updateCompanyField("addressText", e.target.value)
                            }
                          />
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* System Preferences */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">System Preferences</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Timezone</FormLabel>
                          <Select
                            value={settings?.timezone || "UTC"}
                            onChange={(e) =>
                              updateSetting("timezone", e.target.value)
                            }
                          >
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">
                              Eastern Time
                            </option>
                            <option value="America/Chicago">
                              Central Time
                            </option>
                            <option value="America/Denver">
                              Mountain Time
                            </option>
                            <option value="America/Los_Angeles">
                              Pacific Time
                            </option>
                            <option value="Europe/London">London</option>
                            <option value="Europe/Paris">Paris</option>
                            <option value="Asia/Tokyo">Tokyo</option>
                            <option value="Asia/Kolkata">India</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Date Format</FormLabel>
                          <Select
                            value={settings?.dateFormat || "DD/MM/YYYY"}
                            onChange={(e) =>
                              updateSetting("dateFormat", e.target.value)
                            }
                          >
                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Currency</FormLabel>
                          <Select
                            value={settings?.currency || "USD"}
                            onChange={(e) =>
                              updateSetting("currency", e.target.value)
                            }
                          >
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                            <option value="GBP">GBP (£)</option>
                            <option value="INR">INR (₹)</option>
                            <option value="JPY">JPY (¥)</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Language</FormLabel>
                          <Select
                            value={settings?.language || "en"}
                            onChange={(e) =>
                              updateSetting("language", e.target.value)
                            }
                          >
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                            <option value="hi">Hindi</option>
                          </Select>
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Attendance Settings */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Attendance Settings</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Work Start Time</FormLabel>
                          <Input
                            type="time"
                            value={settings?.workStartTime || "09:00"}
                            onChange={(e) =>
                              updateSetting("workStartTime", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Work End Time</FormLabel>
                          <Input
                            type="time"
                            value={settings?.workEndTime || "17:00"}
                            onChange={(e) =>
                              updateSetting("workEndTime", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Break Duration (minutes)</FormLabel>
                          <NumberInput
                            value={settings?.breakDuration || 60}
                            onChange={(value) =>
                              updateSetting("breakDuration", parseInt(value))
                            }
                            min={0}
                            max={180}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Overtime Threshold (hours)</FormLabel>
                          <NumberInput
                            value={settings?.overtimeThreshold || 8}
                            onChange={(value) =>
                              updateSetting(
                                "overtimeThreshold",
                                parseInt(value),
                              )
                            }
                            min={1}
                            max={24}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl gridColumn="span 2">
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Allow Remote Clock In</FormLabel>
                            <Switch
                              isChecked={settings?.allowRemoteClockIn || false}
                              onChange={(e) =>
                                updateSetting(
                                  "allowRemoteClockIn",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Leave Settings */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Leave Management</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Annual Leave Days</FormLabel>
                          <NumberInput
                            value={settings?.annualLeaveDays || 25}
                            onChange={(value) =>
                              updateSetting("annualLeaveDays", parseInt(value))
                            }
                            min={0}
                            max={365}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Sick Leave Days</FormLabel>
                          <NumberInput
                            value={settings?.sickLeaveDays || 10}
                            onChange={(value) =>
                              updateSetting("sickLeaveDays", parseInt(value))
                            }
                            min={0}
                            max={365}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Maternity Leave Days</FormLabel>
                          <NumberInput
                            value={settings?.maternityLeaveDays || 90}
                            onChange={(value) =>
                              updateSetting(
                                "maternityLeaveDays",
                                parseInt(value),
                              )
                            }
                            min={0}
                            max={365}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Paternity Leave Days</FormLabel>
                          <NumberInput
                            value={settings?.paternityLeaveDays || 15}
                            onChange={(value) =>
                              updateSetting(
                                "paternityLeaveDays",
                                parseInt(value),
                              )
                            }
                            min={0}
                            max={365}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl gridColumn="span 2">
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Leave Approval Required
                            </FormLabel>
                            <Switch
                              isChecked={
                                settings?.leaveApprovalRequired || true
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "leaveApprovalRequired",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Email Settings */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Email Configuration</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>SMTP Host</FormLabel>
                          <Input
                            value={settings?.smtpHost || ""}
                            onChange={(e) =>
                              updateSetting("smtpHost", e.target.value)
                            }
                            placeholder="smtp.gmail.com"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>SMTP Port</FormLabel>
                          <NumberInput
                            value={settings?.smtpPort || 587}
                            onChange={(value) =>
                              updateSetting("smtpPort", parseInt(value))
                            }
                            min={1}
                            max={65535}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>SMTP Username</FormLabel>
                          <Input
                            value={settings?.smtpUser || ""}
                            onChange={(e) =>
                              updateSetting("smtpUser", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>From Email Address</FormLabel>
                          <Input
                            type="email"
                            value={settings?.emailFrom || ""}
                            onChange={(e) =>
                              updateSetting("emailFrom", e.target.value)
                            }
                          />
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Notifications */}
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">Email Notifications</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          {[
                            {
                              key: "newEmployee",
                              label: "New Employee Registration",
                            },
                            { key: "leaveRequests", label: "Leave Requests" },
                            {
                              key: "attendanceAlerts",
                              label: "Attendance Alerts",
                            },
                            { key: "systemUpdates", label: "System Updates" },
                          ].map((item) => (
                            <HStack key={item.key} justify="space-between">
                              <FormLabel mb={0}>{item.label}</FormLabel>
                              <Switch
                                isChecked={
                                  settings?.emailNotifications?.[item.key] ||
                                  false
                                }
                                onChange={(e) =>
                                  updateSetting(
                                    `emailNotifications.${item.key}`,
                                    e.target.checked,
                                  )
                                }
                              />
                            </HStack>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">Push Notifications</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Enable Push Notifications
                            </FormLabel>
                            <Switch
                              isChecked={
                                settings?.pushNotifications?.enabled || false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "pushNotifications.enabled",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                          {settings?.pushNotifications?.enabled && (
                            <>
                              <HStack justify="space-between">
                                <FormLabel mb={0}>Leave Reminders</FormLabel>
                                <Switch
                                  isChecked={
                                    settings?.pushNotifications
                                      ?.leaveReminders || false
                                  }
                                  onChange={(e) =>
                                    updateSetting(
                                      "pushNotifications.leaveReminders",
                                      e.target.checked,
                                    )
                                  }
                                />
                              </HStack>
                              <HStack justify="space-between">
                                <FormLabel mb={0}>Birthday Wishes</FormLabel>
                                <Switch
                                  isChecked={
                                    settings?.pushNotifications
                                      ?.birthdayWishes || false
                                  }
                                  onChange={(e) =>
                                    updateSetting(
                                      "pushNotifications.birthdayWishes",
                                      e.target.checked,
                                    )
                                  }
                                />
                              </HStack>
                            </>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  </VStack>
                </TabPanel>

                {/* Security Settings */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Security Settings</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Minimum Password Length</FormLabel>
                          <NumberInput
                            value={settings?.passwordMinLength || 8}
                            onChange={(value) =>
                              updateSetting(
                                "passwordMinLength",
                                parseInt(value),
                              )
                            }
                            min={6}
                            max={32}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Session Timeout (minutes)</FormLabel>
                          <NumberInput
                            value={settings?.sessionTimeout || 480}
                            onChange={(value) =>
                              updateSetting("sessionTimeout", parseInt(value))
                            }
                            min={15}
                            max={1440}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Login Attempts Limit</FormLabel>
                          <NumberInput
                            value={settings?.loginAttemptsLimit || 5}
                            onChange={(value) =>
                              updateSetting(
                                "loginAttemptsLimit",
                                parseInt(value),
                              )
                            }
                            min={1}
                            max={20}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Two-Factor Authentication
                            </FormLabel>
                            <Switch
                              isChecked={settings?.twoFactorAuth || false}
                              onChange={(e) =>
                                updateSetting("twoFactorAuth", e.target.checked)
                              }
                            />
                          </HStack>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Require Special Characters
                            </FormLabel>
                            <Switch
                              isChecked={
                                settings?.passwordRequireSpecialChars || true
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "passwordRequireSpecialChars",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Require Numbers</FormLabel>
                            <Switch
                              isChecked={
                                settings?.passwordRequireNumbers || true
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "passwordRequireNumbers",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Integrations */}
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">Slack Integration</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Enable Slack Integration
                            </FormLabel>
                            <Switch
                              isChecked={
                                settings?.integrations?.slack?.enabled || false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "integrations.slack.enabled",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                          {settings?.integrations?.slack?.enabled && (
                            <>
                              <FormControl>
                                <FormLabel>Webhook URL</FormLabel>
                                <Input
                                  value={
                                    settings?.integrations?.slack?.webhookUrl ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    updateSetting(
                                      "integrations.slack.webhookUrl",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="https://hooks.slack.com/services/..."
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Channel</FormLabel>
                                <Input
                                  value={
                                    settings?.integrations?.slack?.channel || ""
                                  }
                                  onChange={(e) =>
                                    updateSetting(
                                      "integrations.slack.channel",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="#general"
                                />
                              </FormControl>
                            </>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">Microsoft Teams Integration</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Enable Teams Integration
                            </FormLabel>
                            <Switch
                              isChecked={
                                settings?.integrations?.teams?.enabled || false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "integrations.teams.enabled",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                          {settings?.integrations?.teams?.enabled && (
                            <FormControl>
                              <FormLabel>Webhook URL</FormLabel>
                              <Input
                                value={
                                  settings?.integrations?.teams?.webhookUrl ||
                                  ""
                                }
                                onChange={(e) =>
                                  updateSetting(
                                    "integrations.teams.webhookUrl",
                                    e.target.value,
                                  )
                                }
                                placeholder="https://outlook.office.com/webhook/..."
                              />
                            </FormControl>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  </VStack>
                </TabPanel>

                {/* Payroll Settings */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Payroll Settings</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Pay Period</FormLabel>
                          <Select
                            value={
                              settings?.payrollSettings?.payPeriod || "monthly"
                            }
                            onChange={(e) =>
                              updateSetting(
                                "payrollSettings.payPeriod",
                                e.target.value,
                              )
                            }
                          >
                            <option value="weekly">Weekly</option>
                            <option value="bi-weekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Payroll Cutoff Date</FormLabel>
                          <NumberInput
                            value={
                              settings?.payrollSettings?.payrollCutoffDate || 25
                            }
                            onChange={(value) =>
                              updateSetting(
                                "payrollSettings.payrollCutoffDate",
                                parseInt(value),
                              )
                            }
                            min={1}
                            max={31}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Auto Tax Calculation</FormLabel>
                            <Switch
                              isChecked={
                                settings?.payrollSettings?.taxCalculation ||
                                true
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "payrollSettings.taxCalculation",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Auto Process Payroll</FormLabel>
                            <Switch
                              isChecked={
                                settings?.payrollSettings?.autoProcessPayroll ||
                                false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "payrollSettings.autoProcessPayroll",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Recruitment Settings */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Recruitment Settings</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Interview Rounds</FormLabel>
                          <NumberInput
                            value={
                              settings?.recruitmentSettings?.interviewRounds ||
                              3
                            }
                            onChange={(value) =>
                              updateSetting(
                                "recruitmentSettings.interviewRounds",
                                parseInt(value),
                              )
                            }
                            min={1}
                            max={10}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Offer Expiry Days</FormLabel>
                          <NumberInput
                            value={
                              settings?.recruitmentSettings?.offerExpiryDays ||
                              7
                            }
                            onChange={(value) =>
                              updateSetting(
                                "recruitmentSettings.offerExpiryDays",
                                parseInt(value),
                              )
                            }
                            min={1}
                            max={90}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Auto Shortlisting</FormLabel>
                            <Switch
                              isChecked={
                                settings?.recruitmentSettings?.autoShortlist ||
                                false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "recruitmentSettings.autoShortlist",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                        <FormControl>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>
                              Background Check Required
                            </FormLabel>
                            <Switch
                              isChecked={
                                settings?.recruitmentSettings
                                  ?.backgroundCheckRequired || true
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "recruitmentSettings.backgroundCheckRequired",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Appearance */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Appearance Settings</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={6}
                      >
                        <FormControl>
                          <FormLabel>Primary Color</FormLabel>
                          <Input
                            type="color"
                            value={settings?.theme?.primaryColor || "#3182CE"}
                            onChange={(e) =>
                              updateSetting(
                                "theme.primaryColor",
                                e.target.value,
                              )
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Secondary Color</FormLabel>
                          <Input
                            type="color"
                            value={settings?.theme?.secondaryColor || "#E53E3E"}
                            onChange={(e) =>
                              updateSetting(
                                "theme.secondaryColor",
                                e.target.value,
                              )
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Logo URL</FormLabel>
                          <Input
                            value={settings?.theme?.logoUrl || ""}
                            onChange={(e) =>
                              updateSetting("theme.logoUrl", e.target.value)
                            }
                            placeholder="https://example.com/logo.png"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Favicon URL</FormLabel>
                          <Input
                            value={settings?.theme?.faviconUrl || ""}
                            onChange={(e) =>
                              updateSetting("theme.faviconUrl", e.target.value)
                            }
                            placeholder="https://example.com/favicon.ico"
                          />
                        </FormControl>
                      </Grid>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Features */}
                <TabPanel>
                  <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">Feature Flags</Heading>
                      <Text fontSize="sm" color={mutedColor}>
                        Enable or disable specific features in your HR system
                      </Text>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        {[
                          {
                            key: "advancedReporting",
                            label: "Advanced Reporting & Analytics",
                          },
                          { key: "mobileApp", label: "Mobile App Integration" },
                          {
                            key: "apiAccess",
                            label: "API Access for Third-party Apps",
                          },
                          {
                            key: "multiCompany",
                            label: "Multi-Company Support",
                          },
                          {
                            key: "customFields",
                            label: "Custom Fields & Forms",
                          },
                        ].map((feature) => (
                          <HStack
                            key={feature.key}
                            justify="space-between"
                            p={3}
                            border="1px"
                            borderColor={borderColor}
                            rounded="md"
                          >
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium">{feature.label}</Text>
                              <Badge
                                colorScheme={
                                  settings?.features?.[feature.key]
                                    ? "green"
                                    : "gray"
                                }
                                size="sm"
                              >
                                {settings?.features?.[feature.key]
                                  ? "Enabled"
                                  : "Disabled"}
                              </Badge>
                            </VStack>
                            <Switch
                              isChecked={
                                settings?.features?.[feature.key] || false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  `features.${feature.key}`,
                                  e.target.checked,
                                )
                              }
                              size="lg"
                            />
                          </HStack>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>
                </TabPanel>

                {/* Sidebar Management */}
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    {/* Module Active/Inactive Management */}
                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">Sidebar Navigation Modules</Heading>
                        <Text fontSize="sm" color={mutedColor} mt={2}>
                          All modules and items are{" "}
                          <Badge colorScheme="green" ml={1} mr={1}>
                            ACTIVE
                          </Badge>{" "}
                          by default. Toggle any module or item OFF to disable
                          it for all users.
                        </Text>
                      </CardHeader>
                      <CardBody>
                        {sidebarLoading ? (
                          <Center py={10}>
                            <Spinner size="lg" color="teal.500" />
                          </Center>
                        ) : (
                          <VStack spacing={4} align="stretch">
                            <Alert status="success" variant="left-accent">
                              <AlertIcon />
                              <Box>
                                <AlertTitle fontSize="sm">
                                  Default State: All Active
                                </AlertTitle>
                                <AlertDescription fontSize="xs">
                                  {sidebarModules.length} modules and{" "}
                                  {sidebarModules.reduce(
                                    (sum, m) => sum + (m.items?.length || 0),
                                    0,
                                  )}{" "}
                                  items loaded from database. Toggle any item
                                  OFF to disable it globally.
                                </AlertDescription>
                              </Box>
                            </Alert>

                            <SimpleGrid
                              columns={{ base: 1, xl: 2 }}
                              spacing={4}
                            >
                              {sidebarModules.map((module) => {
                                const isExpanded =
                                  !!expandedModules[module._id];
                                const groupedItems = getGroupedModuleItems(
                                  module.items || [],
                                );
                                const enabledCount =
                                  getModuleEnabledCount(module);
                                const totalCount = module.items?.length || 0;

                                return (
                                  <Card
                                    key={module._id}
                                    variant="outline"
                                    size="sm"
                                    bg={useColorModeValue(
                                      "gray.50",
                                      "gray.700",
                                    )}
                                  >
                                    <CardBody>
                                      <VStack spacing={3} align="stretch">
                                        <Stack
                                          direction={{
                                            base: "column",
                                            md: "row",
                                          }}
                                          justify="space-between"
                                          spacing={{ base: 3, md: 0 }}
                                          align={{
                                            base: "stretch",
                                            md: "center",
                                          }}
                                        >
                                          <VStack
                                            align="start"
                                            spacing={1}
                                            flex={1}
                                          >
                                            <HStack spacing={2} wrap="wrap">
                                              <Heading
                                                size="sm"
                                                fontWeight="bold"
                                              >
                                                {module.heading}
                                              </Heading>
                                              <Badge
                                                colorScheme={
                                                  module.isActive
                                                    ? "green"
                                                    : "gray"
                                                }
                                              >
                                                {module.isActive
                                                  ? "● Active"
                                                  : "○ Inactive"}
                                              </Badge>
                                            </HStack>
                                            <Text
                                              fontSize="xs"
                                              color={mutedColor}
                                            >
                                              {enabledCount}/{totalCount}{" "}
                                              features enabled
                                            </Text>
                                          </VStack>

                                          <HStack
                                            justify="space-between"
                                            spacing={3}
                                          >
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              leftIcon={
                                                isExpanded ? (
                                                  <FiChevronDown />
                                                ) : (
                                                  <FiChevronRight />
                                                )
                                              }
                                              onClick={() =>
                                                toggleModuleExpanded(module._id)
                                              }
                                            >
                                              {isExpanded
                                                ? "Collapse"
                                                : "Expand"}
                                            </Button>
                                            <Switch
                                              isChecked={module.isActive}
                                              onChange={() =>
                                                toggleModuleActive(module._id)
                                              }
                                              colorScheme="green"
                                              size="lg"
                                            />
                                          </HStack>
                                        </Stack>

                                        <HStack spacing={2} wrap="wrap">
                                          <Button
                                            size="xs"
                                            onClick={() =>
                                              setModuleItemsActive(
                                                module._id,
                                                true,
                                              )
                                            }
                                            colorScheme="green"
                                            variant="outline"
                                          >
                                            Enable All
                                          </Button>
                                          <Button
                                            size="xs"
                                            onClick={() =>
                                              setModuleItemsActive(
                                                module._id,
                                                false,
                                              )
                                            }
                                            colorScheme="red"
                                            variant="outline"
                                          >
                                            Disable All
                                          </Button>
                                        </HStack>

                                        <Collapse
                                          in={isExpanded}
                                          animateOpacity
                                        >
                                          <VStack
                                            spacing={4}
                                            align="stretch"
                                            opacity={module.isActive ? 1 : 0.5}
                                          >
                                            {Object.entries(groupedItems).map(
                                              ([groupName, items]) => (
                                                <Box
                                                  key={`${module._id}-${groupName}`}
                                                >
                                                  <Text
                                                    fontSize="xs"
                                                    color={mutedColor}
                                                    textTransform="uppercase"
                                                    letterSpacing="wide"
                                                    fontWeight="bold"
                                                    mb={2}
                                                  >
                                                    {groupName}
                                                  </Text>
                                                  <VStack
                                                    spacing={2}
                                                    align="stretch"
                                                  >
                                                    {items.map((item, idx) => (
                                                      <Stack
                                                        key={`${item.key || item.to || item.label}-${idx}`}
                                                        direction={{
                                                          base: "column",
                                                          md: "row",
                                                        }}
                                                        justify="space-between"
                                                        spacing={{
                                                          base: 2,
                                                          md: 0,
                                                        }}
                                                        p={{ base: 3, md: 2 }}
                                                        borderRadius="md"
                                                        borderWidth="1px"
                                                        borderColor={
                                                          borderColor
                                                        }
                                                        bg={useColorModeValue(
                                                          "white",
                                                          "gray.800",
                                                        )}
                                                      >
                                                        <VStack
                                                          align="start"
                                                          spacing={1}
                                                          flex={1}
                                                          minW={0}
                                                        >
                                                          <Text
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            wordBreak="break-word"
                                                          >
                                                            {item.label}
                                                          </Text>
                                                          <Text
                                                            fontSize="xs"
                                                            color={mutedColor}
                                                            wordBreak="break-all"
                                                          >
                                                            {item.to}
                                                          </Text>
                                                        </VStack>
                                                        <Switch
                                                          isChecked={
                                                            item.isActive !==
                                                            false
                                                          }
                                                          onChange={() => {
                                                            const sourceIndex =
                                                              (
                                                                module.items ||
                                                                []
                                                              ).findIndex(
                                                                (mItem) =>
                                                                  mItem.key ===
                                                                    item.key &&
                                                                  mItem.to ===
                                                                    item.to &&
                                                                  mItem.label ===
                                                                    item.label,
                                                              );
                                                            if (
                                                              sourceIndex >= 0
                                                            ) {
                                                              toggleItemActive(
                                                                module._id,
                                                                sourceIndex,
                                                              );
                                                            }
                                                          }}
                                                          colorScheme="green"
                                                          size="md"
                                                          isDisabled={
                                                            !module.isActive
                                                          }
                                                        />
                                                      </Stack>
                                                    ))}
                                                  </VStack>
                                                </Box>
                                              ),
                                            )}
                                          </VStack>
                                        </Collapse>
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                );
                              })}
                            </SimpleGrid>

                            {sidebarModules.length === 0 && (
                              <Alert status="info">
                                <AlertIcon />
                                No sidebar modules found. Please contact your
                                administrator.
                              </Alert>
                            )}
                          </VStack>
                        )}
                      </CardBody>
                    </Card>

                    {/* Tenant Visibility Overrides */}
                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader>
                        <Stack
                          direction={{ base: "column", md: "row" }}
                          justify="space-between"
                          spacing={{ base: 3, md: 0 }}
                          align={{ base: "stretch", md: "flex-start" }}
                        >
                          <Box flex={1}>
                            <Heading size="md">
                              Tenant Visibility Overrides (RBAC)
                            </Heading>
                            <Text fontSize="sm" color={mutedColor} mt={2}>
                              Manage role-to-feature visibility as a matrix. Use
                              module-level and role-level bulk actions to update
                              quickly.
                            </Text>
                          </Box>
                          <Stack
                            direction={{ base: "column", md: "row" }}
                            spacing={2}
                          >
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="green"
                              onClick={() => applyGlobalRolePreset("all")}
                            >
                              Enable All
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              onClick={() => applyGlobalRolePreset("none")}
                            >
                              Disable All
                            </Button>
                            <Button
                              leftIcon={<FiRefreshCw />}
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              onClick={resetAllOverrides}
                              width={{ base: "full", md: "auto" }}
                              isDisabled={
                                !sidebarOverrides?.overrides?.length ||
                                overridesLoading
                              }
                            >
                              Reset
                            </Button>
                          </Stack>
                        </Stack>
                      </CardHeader>
                      <CardBody>
                        {overridesLoading ? (
                          <Center py={10}>
                            <Spinner size="lg" color="blue.500" />
                          </Center>
                        ) : (
                          <VStack spacing={4} align="stretch">
                            <HStack
                              spacing={2}
                              wrap="nowrap"
                              overflowX="auto"
                              pb={1}
                              css={{
                                "&::-webkit-scrollbar": {
                                  height: "4px",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                  background: "#CBD5E0",
                                  borderRadius: "4px",
                                },
                              }}
                            >
                              <Button
                                size="xs"
                                variant={
                                  rbacRoleFilter === "ALL" ? "solid" : "outline"
                                }
                                onClick={() => setRbacRoleFilter("ALL")}
                                flexShrink={0}
                              >
                                All Roles
                              </Button>
                              {Object.keys(rolePermissions).map((role) => (
                                <Button
                                  key={`filter-${role}`}
                                  size="xs"
                                  variant={
                                    rbacRoleFilter === role
                                      ? "solid"
                                      : "outline"
                                  }
                                  colorScheme={
                                    rolePermissions[role]?.color || "gray"
                                  }
                                  onClick={() => setRbacRoleFilter(role)}
                                  flexShrink={0}
                                >
                                  Only {role}
                                </Button>
                              ))}
                            </HStack>

                            {sidebarModules.map((module, moduleIndex) => {
                              const isExpanded =
                                rbacExpandedModules[module._id] ??
                                moduleIndex === 0;
                              const roleColumns = getRoleColumns();

                              return (
                                <Card
                                  key={`rbac-${module._id}`}
                                  variant="outline"
                                  size="sm"
                                  bg={useColorModeValue("white", "gray.700")}
                                >
                                  <CardBody>
                                    <VStack spacing={3} align="stretch">
                                      <Stack
                                        direction={{
                                          base: "column",
                                          md: "row",
                                        }}
                                        justify="space-between"
                                        align={{
                                          base: "stretch",
                                          md: "center",
                                        }}
                                        spacing={2}
                                      >
                                        <HStack spacing={2}>
                                          <Button
                                            size="xs"
                                            variant="ghost"
                                            leftIcon={
                                              isExpanded ? (
                                                <FiChevronDown />
                                              ) : (
                                                <FiChevronRight />
                                              )
                                            }
                                            onClick={() =>
                                              toggleRbacModuleExpanded(
                                                module._id,
                                              )
                                            }
                                          >
                                            {isExpanded ? "Collapse" : "Expand"}
                                          </Button>
                                          <Heading size="sm">
                                            {module.heading}
                                          </Heading>
                                          <Badge
                                            fontSize="xs"
                                            colorScheme="blue"
                                          >
                                            {module.items?.length || 0} features
                                          </Badge>
                                        </HStack>

                                        <Stack
                                          direction={{
                                            base: "column",
                                            md: "row",
                                          }}
                                          spacing={2}
                                          w={{ base: "full", md: "auto" }}
                                        >
                                          <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme="green"
                                            onClick={() =>
                                              applyModuleRolePreset(
                                                module,
                                                "all",
                                              )
                                            }
                                            w={{ base: "full", md: "auto" }}
                                          >
                                            Enable All
                                          </Button>
                                          <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme="red"
                                            onClick={() =>
                                              applyModuleRolePreset(
                                                module,
                                                "none",
                                              )
                                            }
                                            w={{ base: "full", md: "auto" }}
                                          >
                                            Disable All
                                          </Button>
                                          <HStack
                                            spacing={2}
                                            w={{ base: "full", md: "auto" }}
                                          >
                                            <Select
                                              size="xs"
                                              value={
                                                rbacRoleFilter === "ALL"
                                                  ? ""
                                                  : rbacRoleFilter
                                              }
                                              onChange={(e) => {
                                                const role = e.target.value;
                                                if (role) {
                                                  setRoleAcrossModule(
                                                    module,
                                                    role,
                                                    true,
                                                  );
                                                }
                                              }}
                                              maxW={{
                                                base: "full",
                                                md: "180px",
                                              }}
                                              placeholder="Enable role → all"
                                            >
                                              {Object.keys(rolePermissions).map(
                                                (role) => (
                                                  <option
                                                    key={role}
                                                    value={role}
                                                  >
                                                    {role}
                                                  </option>
                                                ),
                                              )}
                                            </Select>
                                          </HStack>
                                        </Stack>
                                      </Stack>

                                      <Collapse in={isExpanded} animateOpacity>
                                        {isMobileRbac ? (
                                          <VStack spacing={3} align="stretch">
                                            {(module.items || []).map(
                                              (item, idx) => {
                                                const eligibleRoles =
                                                  getRolesForItem(
                                                    item.permissions,
                                                  );
                                                const visibleRoles =
                                                  getVisibleRolesForItem(item);
                                                const isVisible =
                                                  getItemVisibility(item.key);

                                                return (
                                                  <Card
                                                    key={`${module._id}-mobile-row-${idx}`}
                                                    variant="outline"
                                                    size="sm"
                                                  >
                                                    <CardBody>
                                                      <VStack
                                                        align="stretch"
                                                        spacing={3}
                                                      >
                                                        <HStack
                                                          justify="space-between"
                                                          align="start"
                                                        >
                                                          <VStack
                                                            align="start"
                                                            spacing={0}
                                                            opacity={
                                                              isVisible
                                                                ? 1
                                                                : 0.65
                                                            }
                                                          >
                                                            <Text
                                                              fontSize="sm"
                                                              fontWeight="medium"
                                                            >
                                                              {item.label}
                                                            </Text>
                                                            <Text
                                                              fontSize="xs"
                                                              color={mutedColor}
                                                              wordBreak="break-all"
                                                            >
                                                              {item.to}
                                                            </Text>
                                                          </VStack>
                                                          <HStack spacing={2}>
                                                            <Text
                                                              fontSize="xs"
                                                              color={mutedColor}
                                                            >
                                                              All roles
                                                            </Text>
                                                            <Switch
                                                              isChecked={
                                                                isVisible &&
                                                                eligibleRoles.length >
                                                                  0 &&
                                                                visibleRoles.length ===
                                                                  eligibleRoles.length
                                                              }
                                                              onChange={(e) =>
                                                                setFeatureAllRoles(
                                                                  item,
                                                                  e.target
                                                                    .checked,
                                                                )
                                                              }
                                                              size="sm"
                                                              colorScheme="green"
                                                              isDisabled={
                                                                !item.key
                                                              }
                                                            />
                                                          </HStack>
                                                        </HStack>

                                                        <SimpleGrid
                                                          columns={{
                                                            base: 2,
                                                            sm: 3,
                                                          }}
                                                          spacing={2}
                                                        >
                                                          {roleColumns.map(
                                                            (role) => {
                                                              const isOn =
                                                                visibleRoles.includes(
                                                                  role,
                                                                );
                                                              return (
                                                                <HStack
                                                                  key={`${module._id}-mobile-${item.key || idx}-${role}`}
                                                                  justify="space-between"
                                                                  borderWidth="1px"
                                                                  borderColor={
                                                                    borderColor
                                                                  }
                                                                  borderRadius="md"
                                                                  px={2}
                                                                  py={1}
                                                                >
                                                                  <Text
                                                                    fontSize="xs"
                                                                    fontWeight="medium"
                                                                    noOfLines={
                                                                      1
                                                                    }
                                                                  >
                                                                    {role}
                                                                  </Text>
                                                                  <Switch
                                                                    isChecked={
                                                                      isOn
                                                                    }
                                                                    onChange={() =>
                                                                      toggleItemRoleVisibility(
                                                                        item,
                                                                        role,
                                                                      )
                                                                    }
                                                                    size="sm"
                                                                    colorScheme="blue"
                                                                    isDisabled={
                                                                      !item.key
                                                                    }
                                                                  />
                                                                </HStack>
                                                              );
                                                            },
                                                          )}
                                                        </SimpleGrid>
                                                      </VStack>
                                                    </CardBody>
                                                  </Card>
                                                );
                                              },
                                            )}
                                          </VStack>
                                        ) : (
                                          <TableContainer
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                            borderRadius="md"
                                            maxH="420px"
                                            overflowY="auto"
                                            overflowX="auto"
                                          >
                                            <Table size="sm" variant="simple">
                                              <Thead
                                                position="sticky"
                                                top={0}
                                                zIndex={3}
                                              >
                                                <Tr
                                                  bg={useColorModeValue(
                                                    "gray.100",
                                                    "gray.700",
                                                  )}
                                                >
                                                  <Th
                                                    position="sticky"
                                                    left={0}
                                                    zIndex={4}
                                                    bg={useColorModeValue(
                                                      "gray.100",
                                                      "gray.700",
                                                    )}
                                                    minW="260px"
                                                  >
                                                    Feature
                                                  </Th>
                                                  {roleColumns.map((role) => (
                                                    <Th
                                                      key={`col-${module._id}-${role}`}
                                                      textAlign="center"
                                                    >
                                                      {role}
                                                    </Th>
                                                  ))}
                                                  <Th textAlign="center">
                                                    All Roles
                                                  </Th>
                                                </Tr>
                                              </Thead>
                                              <Tbody>
                                                {(module.items || []).map(
                                                  (item, idx) => {
                                                    const eligibleRoles =
                                                      getRolesForItem(
                                                        item.permissions,
                                                      );
                                                    const visibleRoles =
                                                      getVisibleRolesForItem(
                                                        item,
                                                      );
                                                    const isVisible =
                                                      getItemVisibility(
                                                        item.key,
                                                      );

                                                    return (
                                                      <Tr
                                                        key={`${module._id}-row-${idx}`}
                                                      >
                                                        <Td
                                                          position="sticky"
                                                          left={0}
                                                          zIndex={2}
                                                          bg={useColorModeValue(
                                                            "white",
                                                            "gray.800",
                                                          )}
                                                          opacity={
                                                            isVisible ? 1 : 0.6
                                                          }
                                                        >
                                                          <VStack
                                                            align="start"
                                                            spacing={0}
                                                          >
                                                            <Text
                                                              fontSize="sm"
                                                              fontWeight="medium"
                                                              wordBreak="break-word"
                                                            >
                                                              {item.label}
                                                            </Text>
                                                            <Text
                                                              fontSize="xs"
                                                              color={mutedColor}
                                                            >
                                                              {item.to}
                                                            </Text>
                                                          </VStack>
                                                        </Td>

                                                        {roleColumns.map(
                                                          (role) => {
                                                            const isOn =
                                                              visibleRoles.includes(
                                                                role,
                                                              );

                                                            return (
                                                              <Td
                                                                key={`${module._id}-${item.key || idx}-${role}`}
                                                                textAlign="center"
                                                              >
                                                                <Switch
                                                                  isChecked={
                                                                    isOn
                                                                  }
                                                                  onChange={() =>
                                                                    toggleItemRoleVisibility(
                                                                      item,
                                                                      role,
                                                                    )
                                                                  }
                                                                  size="sm"
                                                                  colorScheme="blue"
                                                                  isDisabled={
                                                                    !item.key
                                                                  }
                                                                />
                                                              </Td>
                                                            );
                                                          },
                                                        )}

                                                        <Td textAlign="center">
                                                          <Switch
                                                            isChecked={
                                                              isVisible &&
                                                              eligibleRoles.length >
                                                                0 &&
                                                              visibleRoles.length ===
                                                                eligibleRoles.length
                                                            }
                                                            onChange={(e) =>
                                                              setFeatureAllRoles(
                                                                item,
                                                                e.target
                                                                  .checked,
                                                              )
                                                            }
                                                            size="sm"
                                                            colorScheme="green"
                                                            isDisabled={
                                                              !item.key
                                                            }
                                                          />
                                                        </Td>
                                                      </Tr>
                                                    );
                                                  },
                                                )}
                                              </Tbody>
                                            </Table>
                                          </TableContainer>
                                        )}
                                      </Collapse>
                                    </VStack>
                                  </CardBody>
                                </Card>
                              );
                            })}

                            {sidebarModules.length === 0 && (
                              <Alert status="warning">
                                <AlertIcon />
                                No sidebar modules available. Run the seed
                                script to initialize the sidebar configuration.
                              </Alert>
                            )}
                          </VStack>
                        )}
                      </CardBody>
                    </Card>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Box>
          </Stack>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default Settings;
